import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, CircleAlert, Clock, Forward, Inbox, MailCheck, Plus, RefreshCw, ShieldCheck, Trash2, X } from 'lucide-react';
import type { EmailAlias } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { Favicon } from './Avatar';
import { confirmDialog } from './Dialogs';
import { Button, cn, Empty, IconButton, Input, Modal, Textarea, Toggle } from './ui';

/** Settings → Aliases: addresses that forward to outside accounts. Administrators: any hosted domain; domain admins: theirs. */
export function AliasesPanel() {
  const qc = useQueryClient();
  const me = useMe().data;
  const canManage = me?.user.role === 'admin' || (me?.adminDomains?.length ?? 0) > 0;
  const q = useQuery({ queryKey: ['admin-aliases'], queryFn: api.aliases, refetchInterval: 30_000, enabled: canManage });
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers, enabled: canManage });
  const domains = useQuery({ queryKey: ['admin-domains'], queryFn: api.adminDomains, enabled: canManage });
  const [editing, setEditing] = useState<EmailAlias | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-aliases'] });
  const userName = (id?: string) => users.data?.find((u) => u.id === id)?.displayName;

  if (me && !canManage) return <Empty icon={<ShieldCheck />} title="Administrators only" />;
  const aliases = q.data?.aliases ?? [];

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="text-[16px] font-semibold">Aliases</h2>
          <p className="mt-0.5 max-w-xl text-[12.5px] text-muted">
            Create an address on any of your domains that forwards to outside email accounts — optionally keeping a copy in someone's DTS Mail inbox.
            Forwarding goes through Cloudflare, so messages keep their original sender and aren't flagged as spam.
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>New alias</Button>
      </div>

      {q.data?.warning && (
        <div className="mb-3 flex gap-2 rounded-xl bg-warn/10 px-3 py-2 text-[12.5px] text-warn"><CircleAlert className="mt-0.5 size-4 shrink-0" />{q.data.warning}</div>
      )}

      {aliases.length === 0 ? (
        <div className="glass rounded-2xl"><Empty icon={<Forward />} title="No aliases yet" hint="For example sales@yourdomain.com → you@gmail.com, or info@ → two people at once." /></div>
      ) : (
        <div className="space-y-2">
          {aliases.map((a) => (
            <div key={a.address} className={cn('glass flex items-center gap-4 rounded-2xl px-4 py-3', !a.enabled && 'opacity-60')}>
              <Favicon domain={a.domain} size={20} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[14px] font-semibold">
                  {a.address}
                  {!a.enabled && <span className="rounded-full bg-line px-2 py-px text-[11px] font-medium text-muted">paused</span>}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px]">
                  <ArrowRight className="size-3.5 text-faint" />
                  {a.forwardTo.map((f) => <Destination key={f.email} email={f.email} status={f.status} onResent={refresh} />)}
                  {a.keepCopyUserId && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-accent"><Inbox className="size-3" />copy to {userName(a.keepCopyUserId) ?? 'mailbox'}</span>
                  )}
                </div>
                {a.note && <div className="mt-1 text-[11.5px] text-faint">{a.note}</div>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>Edit</Button>
              <IconButton label="Delete alias" onClick={async () => {
                if (!(await confirmDialog(`Delete ${a.address}?`, 'Mail to this address will go to the domain\'s catch-all instead.', { confirmLabel: 'Delete', danger: true }))) return;
                await api.deleteAlias(a.address).catch((e) => toast.error(errorMessage(e)));
                refresh();
              }}><Trash2 /></IconButton>
            </div>
          ))}
        </div>
      )}

      <AliasEditor
        value={editing}
        onClose={() => setEditing(null)}
        domains={(domains.data ?? []).map((d) => d.name)}
        users={(users.data ?? []).map((u) => ({ id: u.id, name: u.displayName }))}
        onSaved={refresh}
      />
    </div>
  );
}

function Destination({ email, status, onResent }: { email: string; status: EmailAlias['forwardTo'][number]['status']; onResent: () => void }) {
  const [busy, setBusy] = useState(false);
  const verified = status === 'verified';
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border py-0.5 pl-2 pr-1', verified ? 'border-ok/30 bg-ok/10 text-ok' : 'border-warn/30 bg-warn/10 text-warn')}
      title={verified ? 'Verified — Cloudflare forwards to this address' : 'Waiting for this person to click the verification link Cloudflare emailed them. Mail is not forwarded until then.'}>
      {verified ? <MailCheck className="size-3" /> : <Clock className="size-3" />}
      {email}
      {!verified && status !== 'unknown' && (
        <button disabled={busy} className="rounded-full px-1 text-[11px] font-semibold underline-offset-2 hover:underline" onClick={async () => {
          setBusy(true);
          try {
            await api.resendAliasVerification(email);
            toast.success(`Verification email sent to ${email}`);
            onResent();
          } catch (e) {
            toast.error(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}>{busy ? <RefreshCw className="size-3 animate-spin" /> : 'resend'}</button>
      )}
    </span>
  );
}

function AliasEditor({ value, onClose, domains, users, onSaved }: {
  value: EmailAlias | 'new' | null; onClose: () => void; domains: string[]; users: { id: string; name: string }[]; onSaved: () => void;
}) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [local, setLocal] = useState('');
  const [domain, setDomain] = useState('');
  const [targets, setTargets] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [keepCopy, setKeepCopy] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLocal(existing?.localPart ?? '');
    setDomain(existing?.domain ?? '');
    setTargets(existing?.forwardTo.map((f) => f.email) ?? []);
    setKeepCopy(existing?.keepCopyUserId ?? '');
    setEnabled(existing?.enabled ?? true);
    setNote(existing?.note ?? '');
    setDraft('');
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const d = domain || domains[0] || '';
  const address = existing?.address ?? (local.trim() ? `${local.trim().toLowerCase()}@${d}` : '');
  const addTarget = () => {
    const parts = draft.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => x.includes('@'));
    if (parts.length) setTargets([...new Set([...targets, ...parts])]);
    setDraft('');
  };

  const save = async () => {
    const all = [...new Set([...targets, ...draft.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => x.includes('@'))])];
    setBusy(true);
    try {
      const r = await api.saveAlias(address, { forwardTo: all, keepCopyUserId: keepCopy || null, enabled, note: note.trim() || undefined });
      if (r.warning) toast.warning(r.warning, { duration: 12000 });
      else if (r.verificationSentTo.length)
        toast.success('Alias saved', { description: `Cloudflare emailed a verification link to ${r.verificationSentTo.join(', ')}. Forwarding starts once they click it.`, duration: 10000 });
      else toast.success('Alias saved');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!value} onClose={onClose} width={560} title={<span className="flex items-center gap-2"><Forward className="size-4 text-accent" />{isNew ? 'New alias' : `Edit ${existing?.address}`}</span>}>
      <div className="space-y-4 p-5">
        {isNew && (
          <div>
            <div className="text-[12px] text-muted">Alias address</div>
            <div className="mt-1 flex items-center gap-1.5">
              <Input autoFocus value={local} onChange={(e) => setLocal(e.target.value.replace(/[@\s+]/g, ''))} placeholder="sales" className="flex-1" />
              <span className="text-muted">@</span>
              <select value={d} onChange={(e) => setDomain(e.target.value)} className="h-9 rounded-xl border border-line bg-raised px-2 text-[13.5px]">
                {domains.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
        )}
        <div>
          <div className="text-[12px] text-muted">Forward to (outside addresses)</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {targets.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full border border-line-strong bg-raised py-0.5 pl-2 pr-1 text-[12.5px]">
                {t}
                <button className="rounded-full p-0.5 text-faint hover:text-fg" onClick={() => setTargets(targets.filter((x) => x !== t))}><X className="size-3" /></button>
              </span>
            ))}
          </div>
          <div className="mt-1.5 flex gap-2">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ',') && (e.preventDefault(), addTarget())} placeholder="name@gmail.com" />
            <Button variant="soft" onClick={addTarget} disabled={!draft.includes('@')}>Add</Button>
          </div>
          <p className="mt-1.5 text-[11.5px] text-faint">Each new address gets a one-time verification email from Cloudflare. Forwarding to it starts after they click the link.</p>
        </div>
        <label className="block text-[12px] text-muted">
          Also keep a copy in
          <select value={keepCopy} onChange={(e) => setKeepCopy(e.target.value)} className="mt-1 h-9 w-full rounded-xl border border-line bg-raised px-2 text-[13.5px] text-fg">
            <option value="">No copy — forward only</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}'s DTS Mail inbox</option>)}
          </select>
        </label>
        <label className="block text-[12px] text-muted">Note (optional)<Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Website contact form" /></label>
        <Toggle label="Active" hint="Paused aliases stop forwarding; mail goes to the domain's catch-all." checked={enabled} onChange={setEnabled} />
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!address || (targets.length === 0 && !draft.includes('@') && !keepCopy)} onClick={save}>Save alias</Button>
      </div>
    </Modal>
  );
}
